import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, ShieldAlert, MessageSquare, Paperclip, FileText, Image as ImageIcon, Download, Bell, BellOff, Camera } from "lucide-react";

/**
 * V160 — Portal message thread.
 *
 * Renders inside PortalPage (parent + tutor). Server-side PII redaction
 * means the receiver NEVER sees the sender's phone/email even if typed —
 * the response body already carries the redacted text.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API });

const POLL_INTERVAL_MS = 8000;

export default function PortalMessages({ party, token, assignmentId }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [piiHint, setPiiHint] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [pushStatus, setPushStatus] = useState({ enabled: false, subscribed: false, endpoint: null, busy: false });
    const listRef = useRef(null);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);

    const ATTACHMENT_ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.heic,image/*,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    const ATTACHMENT_MAX_MB = 10;

    const reload = useCallback(async () => {
        try {
            const r = await publicApi.get(`/portal/${party}/${token}/messages?_=${Date.now()}`);
            setItems(r.data?.items || []);
        } catch (_e) { /* transient */ }
        finally { setLoading(false); }
    }, [party, token]);

    // Initial + poll.
    useEffect(() => {
        reload();
        const iv = setInterval(reload, POLL_INTERVAL_MS);
        return () => clearInterval(iv);
    }, [reload]);

    // V160.2 — presence heartbeat on window focus / visibility change so a
    // recipient who *just* opened the tab is immediately considered online
    // and doesn't trigger a false-positive "offline" push to admin.
    useEffect(() => {
        const ping = () => {
            publicApi.post(`/portal/${party}/${token}/presence`).catch(() => {});
        };
        const onFocus = () => ping();
        const onVis = () => { if (document.visibilityState === "visible") ping(); };
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVis);
        return () => {
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [party, token]);

    // Autoscroll on new items.
    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [items.length]);

    // Live PII hint as user types — soft guardrail (server enforces).
    useEffect(() => {
        const t = draft || "";
        const hasDigits = /(?:\+?\d[\s\-().]*){7,}/.test(t);
        const hasEmail = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/.test(t);
        const hasExtUrl = /\bhttps?:\/\/(?!(?:www\.)?anandtutorials\.com)/i.test(t);
        setPiiHint(hasDigits || hasEmail || hasExtUrl);
    }, [draft]);

    // V161 — Detect existing push subscription + expose enable/disable toggle.
    useEffect(() => {
        let cancelled = false;
        async function init() {
            const supported = typeof window !== "undefined"
                && "serviceWorker" in navigator
                && "PushManager" in window
                && "Notification" in window;
            if (!supported) return;
            try {
                const cfg = await publicApi.get(`/portal/${party}/${token}/push/public-key`);
                if (cancelled) return;
                if (!cfg.data?.enabled) return;
                const reg = await navigator.serviceWorker.register("/portal-sw.js", { scope: "/portal/" });
                const existing = await reg.pushManager.getSubscription();
                setPushStatus((p) => ({
                    ...p,
                    enabled: true,
                    publicKey: cfg.data.public_key,
                    subscribed: !!existing,
                    endpoint: existing?.endpoint || null,
                    reg,
                }));
            } catch (_e) { /* SW registration blocked → silent */ }
        }
        init();
        return () => { cancelled = true; };
    }, [party, token]);

    const enablePush = useCallback(async () => {
        setPushStatus((p) => ({ ...p, busy: true }));
        try {
            const permission = await Notification.requestPermission();
            if (permission !== "granted") {
                toast("Notifications blocked in your browser. Enable them in site settings.", { icon: "🔕" });
                return;
            }
            const { reg, publicKey } = pushStatus;
            const sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: b64urlToUint8Array(publicKey),
            });
            const json = sub.toJSON();
            await publicApi.post(`/portal/${party}/${token}/push/subscribe`, {
                endpoint: json.endpoint,
                keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
                user_agent: navigator.userAgent,
            });
            setPushStatus((p) => ({ ...p, subscribed: true, endpoint: json.endpoint }));
            toast.success("Notifications enabled — we'll ping you when there's a new message.");
        } catch (_e) {
            toast.error("Could not enable notifications.");
        } finally {
            setPushStatus((p) => ({ ...p, busy: false }));
        }
    }, [party, token, pushStatus]);

    const disablePush = useCallback(async () => {
        setPushStatus((p) => ({ ...p, busy: true }));
        try {
            const { reg, endpoint } = pushStatus;
            const sub = await reg.pushManager.getSubscription();
            if (sub) await sub.unsubscribe();
            if (endpoint) {
                await publicApi.post(`/portal/${party}/${token}/push/unsubscribe`, { endpoint });
            }
            setPushStatus((p) => ({ ...p, subscribed: false, endpoint: null }));
            toast("Notifications turned off.", { icon: "🔕" });
        } catch (_e) {
            toast.error("Could not turn off notifications.");
        } finally {
            setPushStatus((p) => ({ ...p, busy: false }));
        }
    }, [party, token, pushStatus]);

    const send = async (e) => {
        e?.preventDefault?.();
        const body = draft.trim();
        if (!body || sending) return;
        setSending(true);
        try {
            const r = await publicApi.post(`/portal/${party}/${token}/messages`, { body });
            setDraft("");
            if (r.data?.contains_pii) {
                toast("Your message contained contact details — they've been hidden. Please use the portal for all coordination.", { icon: "🛡️", duration: 5500 });
            }
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send message");
        } finally {
            setSending(false);
        }
    };


    const handleAttachmentPick = () => fileInputRef.current?.click();
    const handleCameraPick = () => cameraInputRef.current?.click();

    const handleAttachmentChange = async (e) => {
        const file = e.target.files?.[0];
        // Reset immediately so choosing the SAME file twice fires onChange.
        e.target.value = "";
        if (!file || uploading) return;
        if (file.size > ATTACHMENT_MAX_MB * 1024 * 1024) {
            toast.error(`File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max is ${ATTACHMENT_MAX_MB} MB.`);
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await publicApi.post(`/portal/${party}/${token}/messages/attachments`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (r.data?.filename_had_pii) {
                toast("Your file name contained contact details — they've been hidden.", { icon: "🛡️", duration: 5500 });
            } else {
                toast.success("File sent");
            }
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <section data-testid={`portal-messages-${party}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2 flex items-center gap-1.5">
                <MessageSquare className="w-3 h-3" /> Messages
                {items.length > 0 && <span className="text-slate-400 font-normal">· {items.length}</span>}
                {pushStatus.enabled && (
                    <button
                        type="button"
                        onClick={pushStatus.subscribed ? disablePush : enablePush}
                        disabled={pushStatus.busy}
                        data-testid={pushStatus.subscribed ? "portal-messages-push-disable" : "portal-messages-push-enable"}
                        data-cm-compact
                        title={pushStatus.subscribed
                            ? "Notifications on — click to turn off"
                            : "Get pinged when a new message arrives"}
                        className={`ml-auto inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full transition-colors ${
                            pushStatus.subscribed
                                ? "bg-cyan-50 border border-cyan-200 text-cyan-800 hover:bg-cyan-100"
                                : "bg-white border border-slate-200 text-slate-600 hover:border-cyan-300 hover:text-cyan-800"
                        }`}
                    >
                        {pushStatus.busy ? <Loader2 className="w-3 h-3 animate-spin" /> :
                         pushStatus.subscribed ? <Bell className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
                        {pushStatus.subscribed ? "Notifications on" : "Enable alerts"}
                    </button>
                )}
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                {/* Thread */}
                <div ref={listRef} className="max-h-80 overflow-y-auto p-3 space-y-2 bg-slate-50" data-testid="portal-messages-list">
                    {loading ? (
                        <div className="text-center text-xs text-slate-400 py-4"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div>
                    ) : items.length === 0 ? (
                        <div className="text-center text-xs text-slate-400 py-8">
                            No messages yet. Say hello 👋 · Everything you type stays inside Anand Tutorials.
                        </div>
                    ) : items.map((m) => (
                        <Bubble key={m.id} message={m} party={party} token={token} />
                    ))}
                </div>

                {/* Composer */}
                <form onSubmit={send} className="border-t border-slate-200 p-2 bg-white">
                    {piiHint && (
                        <div className="mb-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 flex items-start gap-1.5" data-testid="portal-messages-pii-hint">
                            <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span>We'll hide phone numbers, emails and external links automatically — please share those only through Anand Tutorials support.</span>
                        </div>
                    )}
                    <div className="flex items-end gap-2">
                        <button
                            type="button"
                            onClick={handleAttachmentPick}
                            disabled={uploading}
                            data-testid="portal-messages-attach"
                            data-cm-compact
                            title="Attach worksheet / answer sheet (PDF, image, DOC · 10 MB max)"
                            aria-label="Attach file"
                            className="inline-flex items-center justify-center bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg w-11 h-11 disabled:opacity-60 shrink-0"
                        >
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                        </button>
                        <button
                            type="button"
                            onClick={handleCameraPick}
                            disabled={uploading}
                            data-testid="portal-messages-camera"
                            data-cm-compact
                            title="Take a photo (camera)"
                            aria-label="Take a photo"
                            className="sm:hidden inline-flex items-center justify-center bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg w-11 h-11 disabled:opacity-60 shrink-0"
                        >
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={ATTACHMENT_ACCEPT}
                            className="hidden"
                            onChange={handleAttachmentChange}
                            data-testid="portal-messages-attach-input"
                        />
                        <input
                            ref={cameraInputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={handleAttachmentChange}
                            data-testid="portal-messages-camera-input"
                        />
                        <textarea
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                            }}
                            placeholder="Type a message…"
                            data-testid="portal-messages-input"
                            rows={2}
                            maxLength={4000}
                            className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none resize-none"
                        />
                        <button
                            type="submit"
                            disabled={!draft.trim() || sending}
                            data-testid="portal-messages-send"
                            aria-label="Send message"
                            className="inline-flex items-center justify-center gap-1 bg-[#0F172A] text-white text-sm font-semibold px-3 py-2 rounded-lg hover:bg-[#1E293B] disabled:opacity-60 shrink-0 w-11 h-11 sm:w-auto"
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
}

function Bubble({ message, party, token }) {
    const mine = message.mine;
    const isAdmin = message.sender === "admin";
    const senderLabel = isAdmin ? "Anand Tutorials" : (message.sender === "parent" ? "Parent" : "Tutor");
    const attachments = message.attachments || [];

    if (isAdmin) {
        return (
            <div className="flex justify-center">
                <div className="max-w-[85%] text-[11px] bg-cyan-50 border border-cyan-200 text-cyan-900 rounded-xl px-3 py-1.5 text-center" data-testid="portal-messages-admin">
                    <span className="font-semibold">Anand Tutorials · </span>{message.body}
                </div>
            </div>
        );
    }

    return (
        <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-3 py-1.5 text-sm ${
                mine
                    ? "bg-cyan-600 text-white rounded-br-sm"
                    : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
            }`}>
                <div className={`text-[9px] uppercase tracking-widest font-bold ${mine ? "text-cyan-100" : "text-slate-400"}`}>
                    {mine ? "You" : senderLabel}
                </div>
                <div className="whitespace-pre-wrap break-words">{message.body}</div>
                {attachments.map((a) => (
                    <AttachmentCard key={a.id} attachment={a} party={party} token={token} mine={mine} />
                ))}
                <div className={`text-[9px] mt-0.5 ${mine ? "text-cyan-100" : "text-slate-400"}`}>
                    {formatTime(message.created_at)}
                </div>
            </div>
        </div>
    );
}

function AttachmentCard({ attachment, party, token, mine }) {
    const isImage = (attachment.content_type || "").startsWith("image/");
    const isPdf = attachment.content_type === "application/pdf";
    const src = `${API}/portal/${party}/${token}/messages/attachments/${attachment.id}`;

    // Images render inline (fetch is public via portal token in path — no auth
    // header needed, so <img src> works directly).
    if (isImage) {
        return (
            <a
                href={src}
                target="_blank"
                rel="noreferrer"
                data-testid={`portal-messages-attachment-${attachment.id}`}
                className="block mt-1.5 rounded-lg overflow-hidden border border-white/20"
            >
                <img
                    src={src}
                    alt={attachment.original_filename}
                    className="max-w-[220px] max-h-[220px] object-cover"
                    loading="lazy"
                />
                <div className={`text-[10px] px-2 py-1 flex items-center gap-1 truncate ${mine ? "bg-white/10" : "bg-slate-50"}`}>
                    <ImageIcon className="w-3 h-3 shrink-0" />
                    <span className="truncate">{attachment.original_filename}</span>
                    <span className="opacity-70">· {formatSize(attachment.size)}</span>
                </div>
            </a>
        );
    }
    // PDFs and everything else — file card with a Download affordance.
    return (
        <a
            href={src}
            target="_blank"
            rel="noreferrer"
            data-testid={`portal-messages-attachment-${attachment.id}`}
            className={`mt-1.5 flex items-center gap-2 rounded-lg px-2.5 py-2 border ${
                mine ? "bg-white/10 border-white/20 hover:bg-white/20" : "bg-slate-50 border-slate-200 hover:bg-slate-100"
            }`}
        >
            <div className={`w-8 h-8 rounded flex items-center justify-center ${mine ? "bg-white/20" : "bg-rose-100 text-rose-700"}`}>
                {isPdf ? <FileText className="w-4 h-4" /> : <Paperclip className="w-4 h-4" />}
            </div>
            <div className="min-w-0 flex-1">
                <div className={`text-xs font-semibold truncate ${mine ? "text-white" : "text-slate-800"}`}>{attachment.original_filename}</div>
                <div className={`text-[10px] ${mine ? "text-cyan-100" : "text-slate-500"}`}>
                    {isPdf ? "PDF" : (attachment.content_type || "File")} · {formatSize(attachment.size)}
                </div>
            </div>
            <Download className={`w-3.5 h-3.5 shrink-0 ${mine ? "text-cyan-100" : "text-slate-500"}`} />
        </a>
    );
}

function formatSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// V161 — VAPID applicationServerKey lives as a base64url string. The push
// spec wants a Uint8Array, so we decode here.
function b64urlToUint8Array(base64Url) {
    const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
    const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
    return arr;
}

function formatTime(iso) {
    try {
        const d = new Date(iso);
        const now = new Date();
        const sameDay = d.toDateString() === now.toDateString();
        if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
}
